import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const CATEGORY_DEFS = [
  { key: 'class_regular', label: 'Regular sessions', types: ['class_regular'] },
  { key: 'class_makeup', label: 'Makeup', types: ['class_makeup'] },
  { key: 'class_holiday', label: 'Holiday', types: ['class_holiday'] },
  { key: 'demo', label: 'Demo', types: ['demo'] },
  { key: 'trial', label: 'Trial', types: ['trial'] },
  { key: 'meeting', label: 'Meetings', types: ['meeting'] },
  { key: 'leave', label: 'Leave / absent', types: ['leave_full', 'leave_partial'] },
  { key: 'secondary_assist', label: 'Secondary assist', types: ['secondary_assist'] },
  { key: 'manual', label: 'Manual / other', types: ['manual'] },
];

function addDaysYmd(ymd, days) {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return dt.toISOString().slice(0, 10);
}

function formatShortDate(ymd) {
  if (!ymd) return '';
  const [, m, d] = ymd.split('-');
  return `${d}/${m}`;
}

function dayCellSx(mark) {
  if (mark?.holidayName) return { bgcolor: '#fff3e0' };
  if (mark?.weeklyOff) return { bgcolor: '#e3f2fd' };
  if (mark?.approvedLeave) return { bgcolor: '#f3e5f5' };
  return {};
}

function round2(n) {
  return Math.round(Number(n || 0) * 100) / 100;
}

function categoryForLine(line) {
  return CATEGORY_DEFS.find((c) => c.types.includes(line.lineType))?.key || 'manual';
}

function CellDetailDialog({
  open,
  onClose,
  categoryLabel,
  date,
  lines,
  editable,
  onPatch,
  onDelete,
  onConfirm,
}) {
  const lockedLeave = (l) => ['leave_full', 'leave_partial'].includes(l.lineType);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {categoryLabel}
        {date ? ` · ${date}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        {!lines?.length ? (
          <Typography color="text.secondary">No items.</Typography>
        ) : (
          <Stack spacing={2}>
            {lines.map((l) => (
              <Paper key={l.id} variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2">{l.title}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {l.lineType}
                  {l.systemHours != null ? ` · system ${l.systemHours}h` : ''}
                  {l.isAdjusted ? ' · adjusted' : ''}
                  {l.leaveDayFraction != null ? ` · day ${l.leaveDayFraction}` : ''}
                  {l.confirmationStatus ? ` · ${l.confirmationStatus}` : ''}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mt={1} alignItems="center">
                  <TextField
                    size="small"
                    type="number"
                    label="Hours"
                    defaultValue={l.hours}
                    disabled={!editable || lockedLeave(l)}
                    inputProps={{ step: 0.25, min: 0, max: 24 }}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (!Number.isNaN(v) && v !== l.hours) onPatch?.(l.id, { hours: v });
                    }}
                    sx={{ width: 110 }}
                  />
                  <TextField
                    size="small"
                    label="Remarks"
                    fullWidth
                    defaultValue={l.remarks || ''}
                    disabled={!editable}
                    onBlur={(e) => {
                      if (e.target.value !== (l.remarks || '')) onPatch?.(l.id, { remarks: e.target.value });
                    }}
                  />
                </Stack>
                {l.lineType === 'secondary_assist' && editable && (
                  <Stack direction="row" spacing={1} mt={1}>
                    <Button size="small" onClick={() => onConfirm?.(l.id, 'confirmed')}>
                      Confirm attended
                    </Button>
                    <Button size="small" onClick={() => onConfirm?.(l.id, 'declined')}>
                      Did not attend
                    </Button>
                  </Stack>
                )}
                {editable && l.isManual && (
                  <Button size="small" color="error" sx={{ mt: 1 }} onClick={() => onDelete?.(l.id)}>
                    Delete
                  </Button>
                )}
              </Paper>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function HoursCell({ sum, count, systemSum, hasAdjusted, onOpen, previewLines, sx }) {
  const [anchor, setAnchor] = useState(null);
  if (!count) {
    return (
      <TableCell align="center" sx={{ color: 'text.disabled', py: 1.25, ...sx }}>
        —
      </TableCell>
    );
  }
  return (
    <TableCell align="center" sx={{ py: 1, px: 0.5, ...sx }}>
      <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="center">
        <Button
          size="small"
          onClick={onOpen}
          sx={{ minWidth: 0, px: 0.75, fontWeight: 600, textTransform: 'none' }}
        >
          {round2(sum)}h
        </Button>
        <Tooltip
          title={
            <Box>
              {(previewLines || []).slice(0, 8).map((l) => (
                <Typography key={l.id} variant="caption" display="block">
                  {l.title}: {l.hours}h
                  {l.systemHours != null && l.systemHours !== l.hours ? ` (sys ${l.systemHours})` : ''}
                </Typography>
              ))}
              {(previewLines || []).length > 8 ? (
                <Typography variant="caption">…and more</Typography>
              ) : null}
              {systemSum != null ? (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  System total: {round2(systemSum)}h
                </Typography>
              ) : null}
            </Box>
          }
        >
          <IconButton
            size="small"
            onMouseEnter={(e) => setAnchor(e.currentTarget)}
            onMouseLeave={() => setAnchor(null)}
            onClick={onOpen}
            aria-label="Details"
          >
            <InfoOutlinedIcon fontSize="inherit" color={hasAdjusted ? 'warning' : 'action'} />
          </IconButton>
        </Tooltip>
      </Stack>
      {count > 1 && (
        <Typography variant="caption" color="text.secondary" display="block">
          {count} items
        </Typography>
      )}
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        sx={{ pointerEvents: 'none' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        disableRestoreFocus
      >
        <Box sx={{ p: 1.5, maxWidth: 280 }}>
          {(previewLines || []).map((l) => (
            <Typography key={l.id} variant="body2">
              {l.title}: <strong>{l.hours}h</strong>
            </Typography>
          ))}
        </Box>
      </Popover>
    </TableCell>
  );
}

/**
 * Category × day timesheet grid (teacher fill UI and supervisor review).
 * @param {{ weekStart: string, lines?: any[] }} week
 * @param {boolean} [editable]
 * @param {(lineId: string, body: object) => void} [onPatch]
 * @param {(lineId: string) => void} [onDelete]
 * @param {(lineId: string, confirmationStatus: string) => void} [onConfirm]
 * @param {string} [emptyMessage]
 * @param {string} [hint]
 */
export default function TimesheetWeekGrid({
  week,
  editable = false,
  onPatch,
  onDelete,
  onConfirm,
  emptyMessage = 'No lines yet.',
  hint,
}) {
  const [detail, setDetail] = useState(null);

  const days = useMemo(() => {
    const start = week?.weekStart;
    if (!start) return [];
    const byDate = new Map((week?.dayMarks?.marks || []).map((m) => [m.date, m]));
    return DAY_NAMES.map((name, i) => {
      const date = addDaysYmd(start, i);
      return { name, date, mark: byDate.get(date) || null };
    });
  }, [week?.weekStart, week?.dayMarks]);

  const grid = useMemo(() => {
    const map = {};
    for (const cat of CATEGORY_DEFS) {
      map[cat.key] = {};
      for (const day of days) map[cat.key][day.date] = [];
    }
    for (const line of week?.lines || []) {
      const cat = categoryForLine(line);
      const date = line.workDate;
      if (!map[cat]) map[cat] = {};
      if (!map[cat][date]) map[cat][date] = [];
      map[cat][date].push(line);
    }
    return map;
  }, [week?.lines, days]);

  const visibleCategories = useMemo(() => {
    return CATEGORY_DEFS.filter((cat) => {
      const byDate = grid[cat.key] || {};
      return Object.values(byDate).some((arr) => arr.length > 0);
    });
  }, [grid]);

  const dayTotals = useMemo(() => {
    const totals = {};
    for (const day of days) {
      let sum = 0;
      for (const cat of CATEGORY_DEFS) {
        for (const l of grid[cat.key]?.[day.date] || []) {
          if (l.lineType === 'secondary_assist' && l.confirmationStatus === 'declined') continue;
          sum += Number(l.hours || 0);
        }
      }
      totals[day.date] = round2(sum);
    }
    return totals;
  }, [grid, days]);

  const detailLines = detail ? grid[detail.categoryKey]?.[detail.date] || [] : [];

  if (!week?.weekStart) return null;

  if (visibleCategories.length === 0 && !(week?.dayMarks?.marks || []).length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <>
      {hint ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {hint}
        </Typography>
      ) : null}
      <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="caption" sx={{ bgcolor: '#e3f2fd', px: 1 }}>Weekly off</Typography>
        <Typography variant="caption" sx={{ bgcolor: '#fff3e0', px: 1 }}>Holiday</Typography>
        <Typography variant="caption" sx={{ bgcolor: '#f3e5f5', px: 1 }}>Approved leave</Typography>
      </Stack>
      {visibleCategories.length === 0 ? (
        <Alert severity="info" sx={{ mb: 1 }}>{emptyMessage}</Alert>
      ) : null}
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, minWidth: 140 }}>Category</TableCell>
              {days.map((day) => (
                <TableCell key={day.date} align="center" sx={{ fontWeight: 700, minWidth: 88, ...dayCellSx(day.mark) }}>
                  <Typography variant="body2" fontWeight={700}>
                    {day.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatShortDate(day.date)}
                  </Typography>
                  {day.mark?.holidayName ? (
                    <Typography variant="caption" display="block">Holiday</Typography>
                  ) : null}
                  {day.mark?.weeklyOff && !day.mark?.holidayName ? (
                    <Typography variant="caption" display="block">Weekly off</Typography>
                  ) : null}
                  {day.mark?.approvedLeave ? (
                    <Typography variant="caption" display="block">
                      {day.mark.approvedLeave.leaveTypeName}
                    </Typography>
                  ) : null}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ fontWeight: 700, minWidth: 72 }}>
                Week
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleCategories.map((cat) => {
              let weekSum = 0;
              return (
                <TableRow key={cat.key} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{cat.label}</TableCell>
                  {days.map((day) => {
                    const lines = grid[cat.key]?.[day.date] || [];
                    const sum = round2(
                      lines.reduce((acc, l) => {
                        if (l.lineType === 'secondary_assist' && l.confirmationStatus === 'declined') {
                          return acc;
                        }
                        return acc + Number(l.hours || 0);
                      }, 0)
                    );
                    weekSum += sum;
                    const systemSum = round2(
                      lines.reduce((acc, l) => acc + Number(l.systemHours ?? l.hours ?? 0), 0)
                    );
                    const hasAdjusted = lines.some((l) => l.isAdjusted);
                    return (
                      <HoursCell
                        key={day.date}
                        sum={sum}
                        count={lines.length}
                        systemSum={systemSum}
                        hasAdjusted={hasAdjusted}
                        previewLines={lines}
                        sx={dayCellSx(day.mark)}
                        onOpen={() =>
                          setDetail({
                            categoryKey: cat.key,
                            date: day.date,
                            label: cat.label,
                          })
                        }
                      />
                    );
                  })}
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {round2(weekSum)}h
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 700 }}>Day total</TableCell>
              {days.map((day) => (
                <TableCell key={day.date} align="center" sx={{ fontWeight: 700, ...dayCellSx(day.mark) }}>
                  {dayTotals[day.date] ? `${dayTotals[day.date]}h` : '—'}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {round2(Object.values(dayTotals).reduce((a, b) => a + b, 0))}h
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <CellDetailDialog
        open={Boolean(detail)}
        onClose={() => setDetail(null)}
        categoryLabel={detail?.label}
        date={detail?.date}
        lines={detailLines}
        editable={editable}
        onPatch={onPatch}
        onDelete={onDelete}
        onConfirm={onConfirm}
      />
    </>
  );
}
